<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { NODE_TYPES, CONDITION_OPERATORS, FLOW_RESTRICTABLE_TOOLS } from '../constants';

const props = defineProps({
  node: { type: Object, default: null },
  teams: { type: Array, default: () => [] },
  flows: { type: Array, default: () => [] },
});
const emit = defineEmits(['update', 'delete', 'close']);

const { t } = useI18n();

const meta = computed(() => (props.node ? NODE_TYPES[props.node.type] : null));

const updateData = patch => {
  emit('update', { ...props.node.data, ...patch });
};

const addOption = () => {
  const options = [...(props.node.data.options || []), { label: '' }];
  updateData({ options });
};
const updateOption = (index, label) => {
  const options = [...(props.node.data.options || [])];
  options[index] = { ...options[index], label };
  updateData({ options });
};
const removeOption = index => {
  const options = (props.node.data.options || []).filter((_, i) => i !== index);
  updateData({ options });
};

const toggleAllowedTool = toolValue => {
  const current = props.node.data.allowed_tools || [];
  const allowed_tools = current.includes(toolValue)
    ? current.filter(v => v !== toolValue)
    : [...current, toolValue];
  updateData({ allowed_tools });
};

const addIntent = () => {
  const intents = [...(props.node.data.intents || []), { label: '' }];
  updateData({ intents });
};
const updateIntent = (index, label) => {
  const intents = [...(props.node.data.intents || [])];
  intents[index] = { ...intents[index], label };
  updateData({ intents });
};
const removeIntent = index => {
  const intents = (props.node.data.intents || []).filter((_, i) => i !== index);
  updateData({ intents });
};

const addHeader = () => {
  const headers = [...(props.node.data.headers || []), { key: '', value: '' }];
  updateData({ headers });
};
const updateHeader = (index, patch) => {
  const headers = [...(props.node.data.headers || [])];
  headers[index] = { ...headers[index], ...patch };
  updateData({ headers });
};
const removeHeader = index => {
  const headers = (props.node.data.headers || []).filter((_, i) => i !== index);
  updateData({ headers });
};
</script>

<template>
  <div class="flex flex-col w-72 shrink-0 gap-4 p-4 overflow-y-auto border-l border-n-weak">
    <template v-if="node && meta">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span
            class="grid rounded size-6 place-content-center text-white text-xs"
            :style="{ backgroundColor: meta.color }"
          >
            <span :class="meta.icon" />
          </span>
          <h3 class="text-sm font-semibold text-n-slate-12">{{ meta.label }}</h3>
        </div>
        <button type="button" class="text-n-slate-10 hover:text-n-slate-12" @click="emit('close')">
          <span class="i-lucide-x" />
        </button>
      </div>

      <div v-if="node.type === 'message'" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.TEXT') }}</label>
        <textarea
          :value="node.data.text"
          rows="5"
          class="reset-base w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 p-2"
          :placeholder="t('BOT_FLOWS.EDITOR.FIELDS.TEXT_PLACEHOLDER')"
          @input="updateData({ text: $event.target.value })"
        />
        <p class="text-[11px] text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.VARIABLE_HINT') }}</p>
      </div>

      <template v-else-if="node.type === 'question'">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.QUESTION_TEXT') }}</label>
          <textarea
            :value="node.data.text"
            rows="3"
            class="reset-base w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 p-2"
            @input="updateData({ text: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.VARIABLE') }}</label>
          <input
            :value="node.data.variable"
            type="text"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            placeholder="nome, cpf, ..."
            @input="updateData({ variable: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.MODE') }}</label>
          <select
            :value="node.data.mode"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            @change="updateData({ mode: $event.target.value })"
          >
            <option value="texto">{{ t('BOT_FLOWS.EDITOR.FIELDS.MODE_TEXT') }}</option>
            <option value="opcoes">{{ t('BOT_FLOWS.EDITOR.FIELDS.MODE_OPTIONS') }}</option>
          </select>
        </div>
        <div v-if="node.data.mode === 'opcoes'" class="flex flex-col gap-2">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.OPTIONS') }}</label>
          <div
            v-for="(option, index) in node.data.options || []"
            :key="index"
            class="flex items-center gap-1"
          >
            <span class="text-xs text-n-slate-10">{{ index + 1 }}.</span>
            <input
              :value="option.label"
              type="text"
              class="reset-base w-full !h-8 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
              @input="updateOption(index, $event.target.value)"
            />
            <button type="button" class="text-n-ruby-10 hover:text-n-ruby-11" @click="removeOption(index)">
              <span class="i-lucide-x" />
            </button>
          </div>
          <Button size="sm" variant="faded" color="slate" :label="t('BOT_FLOWS.EDITOR.FIELDS.ADD_OPTION')" @click="addOption" />
        </div>
      </template>

      <template v-else-if="node.type === 'condition'">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.VARIABLE') }}</label>
          <input
            :value="node.data.variable"
            type="text"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            @input="updateData({ variable: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.OPERATOR') }}</label>
          <select
            :value="node.data.operator"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            @change="updateData({ operator: $event.target.value })"
          >
            <option v-for="op in CONDITION_OPERATORS" :key="op.value" :value="op.value">
              {{ op.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.VALUE') }}</label>
          <input
            :value="node.data.value"
            type="text"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            @input="updateData({ value: $event.target.value })"
          />
        </div>
      </template>

      <template v-else-if="node.type === 'transfer_human'">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.LABEL') }}</label>
          <input
            :value="node.data.label"
            type="text"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            placeholder="comercial, suporte-tecnico, financeiro..."
            @input="updateData({ label: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.TEAM') }}</label>
          <select
            :value="node.data.team_id"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            @change="updateData({ team_id: $event.target.value ? Number($event.target.value) : null })"
          >
            <option :value="null">{{ t('BOT_FLOWS.EDITOR.FIELDS.NO_TEAM') }}</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</option>
          </select>
          <p class="text-[11px] text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.TEAM_HINT') }}</p>
        </div>
      </template>

      <template v-else-if="node.type === 'ask_jhulie'">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.GOAL') }}</label>
          <textarea
            :value="node.data.goal"
            rows="2"
            class="reset-base w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 p-2"
            :placeholder="t('BOT_FLOWS.EDITOR.FIELDS.GOAL_PLACEHOLDER')"
            @input="updateData({ goal: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.SUCCESS_CRITERIA') }}</label>
          <textarea
            :value="node.data.success_criteria"
            rows="2"
            class="reset-base w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 p-2"
            @input="updateData({ success_criteria: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.HANDOFF_CRITERIA') }}</label>
          <textarea
            :value="node.data.handoff_criteria"
            rows="2"
            class="reset-base w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 p-2"
            @input="updateData({ handoff_criteria: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.FALLBACK_MESSAGE') }}</label>
          <textarea
            :value="node.data.fallback_message"
            rows="2"
            class="reset-base w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 p-2"
            @input="updateData({ fallback_message: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.ALLOWED_TOOLS') }}</label>
          <label
            v-for="tool in FLOW_RESTRICTABLE_TOOLS"
            :key="tool.value"
            class="flex items-center gap-2 text-xs text-n-slate-11"
          >
            <input
              type="checkbox"
              :checked="(node.data.allowed_tools || []).includes(tool.value)"
              @change="toggleAllowedTool(tool.value)"
            />
            {{ tool.label }}
          </label>
          <p class="text-[11px] text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.ALLOWED_TOOLS_HINT') }}</p>
        </div>
      </template>

      <template v-else-if="node.type === 'identify_intent'">
        <div class="flex flex-col gap-2">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.INTENTS') }}</label>
          <div
            v-for="(intent, index) in node.data.intents || []"
            :key="index"
            class="flex items-center gap-1"
          >
            <input
              :value="intent.label"
              type="text"
              class="reset-base w-full !h-8 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
              placeholder="Internet com problema..."
              @input="updateIntent(index, $event.target.value)"
            />
            <button type="button" class="text-n-ruby-10 hover:text-n-ruby-11" @click="removeIntent(index)">
              <span class="i-lucide-x" />
            </button>
          </div>
          <Button size="sm" variant="faded" color="slate" :label="t('BOT_FLOWS.EDITOR.FIELDS.ADD_INTENT')" @click="addIntent" />
          <p class="text-[11px] text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.INTENTS_HINT') }}</p>
        </div>
      </template>

      <template v-else-if="node.type === 'http_request'">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.METHOD') }}</label>
          <select
            :value="node.data.method"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            @change="updateData({ method: $event.target.value })"
          >
            <option value="get">GET</option>
            <option value="post">POST</option>
            <option value="put">PUT</option>
            <option value="patch">PATCH</option>
            <option value="delete">DELETE</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.URL') }}</label>
          <input
            :value="node.data.url"
            type="text"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            placeholder="https://..."
            @input="updateData({ url: $event.target.value })"
          />
          <p class="text-[11px] text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.URL_HINT') }}</p>
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.HEADERS') }}</label>
          <div
            v-for="(header, index) in node.data.headers || []"
            :key="index"
            class="flex items-center gap-1"
          >
            <input
              :value="header.key"
              type="text"
              class="reset-base w-full !h-8 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
              placeholder="Authorization"
              @input="updateHeader(index, { key: $event.target.value })"
            />
            <input
              :value="header.value"
              type="text"
              class="reset-base w-full !h-8 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
              placeholder="Bearer ..."
              @input="updateHeader(index, { value: $event.target.value })"
            />
            <button type="button" class="text-n-ruby-10 hover:text-n-ruby-11" @click="removeHeader(index)">
              <span class="i-lucide-x" />
            </button>
          </div>
          <Button size="sm" variant="faded" color="slate" :label="t('BOT_FLOWS.EDITOR.FIELDS.ADD_HEADER')" @click="addHeader" />
        </div>
        <div v-if="['post', 'put', 'patch'].includes(node.data.method)" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.BODY') }}</label>
          <textarea
            :value="node.data.body"
            rows="4"
            class="reset-base w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 p-2 font-mono"
            placeholder='{"cpf": "{{cpf}}"}'
            @input="updateData({ body: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.SAVE_RESPONSE_AS') }}</label>
          <input
            :value="node.data.save_response_as"
            type="text"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            placeholder="resposta_api"
            @input="updateData({ save_response_as: $event.target.value })"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.SAVE_STATUS_AS') }}</label>
          <input
            :value="node.data.save_status_as"
            type="text"
            class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
            placeholder="status_api"
            @input="updateData({ save_status_as: $event.target.value })"
          />
        </div>
      </template>

      <div v-else-if="node.type === 'sub_flow'" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.SUB_FLOW') }}</label>
        <select
          :value="node.data.flow_id"
          class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
          @change="updateData({ flow_id: $event.target.value ? Number($event.target.value) : null })"
        >
          <option :value="null">{{ t('BOT_FLOWS.EDITOR.FIELDS.SELECT_FLOW') }}</option>
          <option v-for="flowItem in flows" :key="flowItem.id" :value="flowItem.id">{{ flowItem.name }}</option>
        </select>
      </div>

      <div v-else-if="node.type === 'delay'" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">{{ t('BOT_FLOWS.EDITOR.FIELDS.SECONDS') }}</label>
        <input
          :value="node.data.seconds"
          type="number"
          min="1"
          max="5"
          class="reset-base w-full !h-9 !mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-1 px-2"
          @input="updateData({ seconds: Number($event.target.value) })"
        />
        <p class="text-[11px] text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.SECONDS_HINT') }}</p>
      </div>

      <p v-else class="text-xs text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.NO_CONFIG') }}</p>

      <Button
        size="sm"
        variant="faded"
        color="ruby"
        icon="i-lucide-trash-2"
        :label="t('BOT_FLOWS.EDITOR.FIELDS.DELETE_NODE')"
        class="mt-2"
        @click="emit('delete')"
      />
    </template>
    <p v-else class="text-xs text-n-slate-10">{{ t('BOT_FLOWS.EDITOR.FIELDS.SELECT_NODE') }}</p>
  </div>
</template>
